import {
  ArrowDownward,
  ArrowUpward,
  NorthEast,
  NorthWest,
  Remove,
  SouthEast,
  SouthWest,
} from '@mui/icons-material';
import { Stack, SvgIconProps, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Direction of a movement, owned by the backend (`PulseMovementIndicator`
 * enum, verbatim). Doubles as the icon choice: `UP`/`DOWN` are straight
 * arrows (trends), the four diagonals are transition arrows, `STABLE` is a
 * dash.
 */
export type MovementDirection =
  | 'UP'
  | 'UP_LEFT'
  | 'UP_RIGHT'
  | 'STABLE'
  | 'DOWN'
  | 'DOWN_LEFT'
  | 'DOWN_RIGHT';

/**
 * The full movement payload sent by the backend for any indicator — the BE
 * `PulseMovementIndicator` DTO, verbatim. The BE owns the semantics
 * (including translation of `text`); the FE only derives icon and color from
 * `direction` + `invertColor`.
 */
export type Movement = {
  /** Display text, rendered as-is — the BE translates it. */
  text: string;
  direction: MovementDirection;
  /**
   * Flips the direction-derived color for higher-is-bad metrics: upward
   * directions read as red, downward as green. Ignored for `STABLE`, which
   * always renders neutral gray.
   */
  invertColor?: boolean;
};

export type MovementIndicatorProps = {
  movement?: Movement | null;
  hideLabel?: boolean;
  size?: 'small' | 'medium';
  /**
   * When provided, the indicator renders as a button (e.g. segment-grid badges
   * open the movement legend drawer). Visuals are unchanged.
   */
  onClick?: () => void;
  'data-testid'?: string;
};

type Tone = 'success' | 'error' | 'neutral';

const ICONS: Record<MovementDirection, (props: SvgIconProps) => JSX.Element> = {
  UP: ArrowUpward,
  UP_LEFT: NorthWest,
  UP_RIGHT: NorthEast,
  STABLE: Remove,
  DOWN: ArrowDownward,
  DOWN_LEFT: SouthWest,
  DOWN_RIGHT: SouthEast,
};

const BASE_TONES: Record<MovementDirection, Tone> = {
  UP: 'success',
  UP_LEFT: 'success',
  UP_RIGHT: 'success',
  STABLE: 'neutral',
  DOWN: 'error',
  DOWN_LEFT: 'error',
  DOWN_RIGHT: 'error',
};

const FLIPPED: Record<Tone, Tone> = {
  success: 'error',
  error: 'success',
  neutral: 'neutral',
};

/**
 * Directional indicator used across Guest Journey (segment-grid cells, movement
 * tables, Vio trend bullets, and KPI card trend chips). Renders the BE-sent
 * `text` with icon + color derived from `direction` and `invertColor`.
 */
const MovementIndicator = ({
  movement,
  hideLabel = false,
  size = 'medium',
  onClick,
  'data-testid': dataTestId = 'MovementIndicator',
}: MovementIndicatorProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // A metric with no comparison carries a null movement (e.g. a benchmark row
  // whose vsDelta the BE sends as null). Render nothing rather than crash — a
  // single null here otherwise takes down the whole route via the error boundary.
  if (!movement) return null;

  const { text, direction, invertColor } = movement;

  const Icon = ICONS[direction];
  const baseTone = BASE_TONES[direction];
  const tone = invertColor ? FLIPPED[baseTone] : baseTone;

  const color =
    tone === 'success'
      ? theme.palette.success.main
      : tone === 'error'
      ? theme.palette.error.main
      : theme.palette.text.secondary;

  const iconSize = size === 'small' ? 16 : 20;

  const children = (
    <>
      <Icon sx={{ fontSize: iconSize }} data-testid={`${dataTestId}.Icon`} />
      {!hideLabel && (
        <Typography
          variant={size === 'small' ? 'caption' : 'body2'}
          sx={{ color }}
        >
          {text}
        </Typography>
      )}
    </>
  );

  if (onClick) {
    return (
      <Stack
        component="button"
        type="button"
        onClick={onClick}
        aria-label={t('movementIndicator.viewDefinition', { label: text })}
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{
          color,
          cursor: 'pointer',
          border: 0,
          padding: 0,
          background: 'transparent',
          font: 'inherit',
        }}
        data-testid={dataTestId}
      >
        {children}
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{ color }}
      data-testid={dataTestId}
    >
      {children}
    </Stack>
  );
};

export default MovementIndicator;
